import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import LeadKanbanCard from "../LeadKanbanCard";
import {
  useGetAllLeadStagesQuery,
  useGetLeadsMutation,
  useUpdateLeadStageMutation,
  useGetCrmPipelinesQuery,
  useGetCrmViewsQuery,
  useDeleteCrmViewMutation,
  useGetLeadCountriesQuery,
} from "../../../../redux/services/crmApi";
import { skipToken } from "@reduxjs/toolkit/query";
import SortByIcon from "../../../../assets/svg/sort-by-icon";
import Header from "../../../../components/Header/Header";
import SearchBar from "../../../../components/SearchBar/SearchBar";
import { visibleScrollbar } from "../../../../utils/functions/get-visible-scrollbar";
import { Lead, Stage, Pipeline, Country } from "../../utils/types";
import { Avatar } from "../../../../components/Avatar/Avatar";
import LeadTypeBadge from "../../../../components/LeadCard/LeadTypeBadge";
import { LeadSuggestionItem } from "../../../Communications/utils/types";
import PulseLoader from "../../../../components/Loader/PulseLoader";
import LeadStageSortPopup from "../LeadStageSortPopup";
import Button from "../../../../components/Button/Button";
import Filters from "../../../../components/Filters/Filters";
import {
  selectCrmLeadsActiveFilterCount,
  selectCrmLeadsFilters,
  setCrmLeadsDuration,
  clearCrmLeadsDuration,
  setCrmLeadsDestinationCountry,
} from "../../../../redux/slices/filterSlice";
import { useSelector, useDispatch } from "react-redux";
import FilterV2Icon from "../../../../assets/svg/filter-v2-icon";
import { cleanObject, getGlobalCrmFilters } from "../../utils/functions";
import {
  setActiveTab,
  setActiveView,
  setPipelines,
  setSelectedViewId,
  setNewlyCreatedViewId,
  setCrmViews,
  removeCrmView,
  setStages,
  appendStageLeads,
  updateLeadInStage,
  updateStageFilters,
  resetStages,
  moveLeadBetweenStages,
  setCollapsedStageIds,
  toggleCollapsedStage,
  clearCollapsedStages,
  setAutoCollapseSignature,
  setStagesLoadedSignature,
  type CrmViewItem,
} from "../../../../redux/slices/crmLeadListSlice";
import { normalizeFilterGroups } from "./utlis/crmViewConstants";
import { RootState } from "../../../../redux/store";
import { useLazyGetLeadListQuery } from "../../../../redux/services/communicationApi";
import { DragDropProvider } from "@dnd-kit/react";
import { customToast } from "../../../../utils/custom-toast/custom-toast";
import LeadStageChangeToLostModal from "../LeadStageChangeToLostModal";
import { isCancelledReasonModalClient } from "../HelpDesk/utils/function";
import { getClientDomain } from "../../../../utils/functions/client-code";
import PlayIcon from "../../../../assets/svg/play-icon";
import {
  buildStagesFromPipelines,
  buildPipelineGroups,
  buildStageInfoRows,
} from "./utlis/functions";
import type { PipelineGroup } from "./utlis/types";
import { KanbanActionBar } from "./components/KanbanActionBar";
import LeadStagesShimmer from "./components/LeadListShimmer/LeadStagesShimmer";
import KanBanHeader from "./components/KanbanHeader";
import DroppableStage from "./components/DroppableStage";
import { ViewToggle } from "./components/ViewToggle";
import { LeadTableListing } from "./components/LeadTableListing";
import EditColumnsModal from "./components/EditColumnsModal";
import {
  getDefaultColumnConfig,
  getDateRangeFromPreset,
  CREATED_DATE_PRESETS,
  type LeadColumnConfig,
} from "./utlis/leadTableColumns";
import { CrmViewTabs } from "./components/CrmViewTabs";
import { CrmCreateEditView } from "./components/CrmCreateEditView";
import CrmDeleteViewModal from "./components/CrmDeleteViewModal";
import LeadStagesInfoModal from "./components/LeadStagesInfoModal";
import InfoBtnIcon from "../../../../assets/svg/info-btn-icon";
import ExportContactsModal from "./components/ExportContactsModal";
import ExportQueuedModal from "./components/ExportQueuedModal";
import { useCreateDataExportMutation } from "../../../../redux/services/settingsApi";
import { setDrawer as setCrmDrawer } from "../../../../redux/slices/drawerSlice";
import PlusFilledIcon from "../../../../assets/svg/plus-filled-icon";
import { TruncatedTextTooltip } from "../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import { useSearchTracking } from "../../../../utils/amplitude/hooks/useSearchTracking";
import { trackEvents } from "../../../../utils/amplitude/amplitude";
import { CRM_EVENTS } from "../../utils/crmTracking.constants";

const LeadList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { trackSearchPerformed } = useSearchTracking("CRM");

  const stages = useSelector((state: RootState) => state.crmLeadList.stages);
  const pipelines = useSelector(
    (state: RootState) => state.crmLeadList.pipelines
  );
  const activeTab = useSelector(
    (state: RootState) => state.crmLeadList.activeTab
  );
  const activeView = useSelector(
    (state: RootState) => state.crmLeadList.activeView
  );
  const selectedViewId = useSelector(
    (state: RootState) => state.crmLeadList.selectedViewId
  );
  const viewRefreshTrigger = useSelector(
    (state: RootState) => state.crmLeadList.viewRefreshTrigger
  );
  const newlyCreatedViewId = useSelector(
    (state: RootState) => state.crmLeadList.newlyCreatedViewId
  );
  const crmViews = useSelector(
    (state: RootState) => state.crmLeadList.crmViews
  );
  const collapsedStageIds = useSelector(
    (state: RootState) => state.crmLeadList.collapsedStageIds
  );
  const autoCollapseSignature = useSelector(
    (state: RootState) => state.crmLeadList.autoCollapseSignature
  );
  const stagesLoadedSignature = useSelector(
    (state: RootState) => state.crmLeadList.stagesLoadedSignature
  );
  const leadsFilters = useSelector(selectCrmLeadsFilters);
  const filterCount = useSelector(selectCrmLeadsActiveFilterCount);

  const [fetchLeads] = useGetLeadsMutation();
  const [getLeadList] = useLazyGetLeadListQuery();
  const [updateLeadStage] = useUpdateLeadStageMutation();
  const { data: crmViewsData } = useGetCrmViewsQuery({});
  const [deleteCrmViewApi] = useDeleteCrmViewMutation();
  const [createDataExport, { isLoading: isExporting }] =
    useCreateDataExportMutation();

  const isMansionstudent = getClientDomain() === "mansionstudent";
  const { data: countriesData } = useGetLeadCountriesQuery(
    isMansionstudent ? {} : skipToken
  );

  const hasPreselectedCountryRef = useRef(false);

  useEffect(() => {
    if (hasPreselectedCountryRef.current) return;
    if (!isMansionstudent) return;
    if (leadsFilters.destinationCountry.value.length > 0) return;
    if (!countriesData?.length) return;

    const uk = countriesData.find(
      (c: Country) => c.name.toLowerCase() === "united kingdom"
    );
    if (!uk) return;

    hasPreselectedCountryRef.current = true;
    dispatch(
      setCrmLeadsDestinationCountry({
        value: [uk.id as unknown as string],
        displayValue: [uk.name],
      })
    );
  }, [countriesData, leadsFilters.destinationCountry.value, dispatch, isMansionstudent]);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [loadingStages, setLoadingStages] = useState<Record<string, boolean>>(
    {}
  );
  const fetchingStagesRef = useRef<Record<string, boolean>>({});
  const [openSortPopup, setOpenSortPopup] = useState<string | null>(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isEditColumnsOpen, setIsEditColumnsOpen] = useState(false);
  const [isStageInfoOpen, setIsStageInfoOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExportQueuedOpen, setIsExportQueuedOpen] = useState(false);
  const [exportedFile, setExportedFile] = useState<{
    name: string;
    format: string;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [columnConfig, setColumnConfig] = useState<LeadColumnConfig[]>(
    getDefaultColumnConfig
  );
  const [pinnedColumns, setPinnedColumns] = useState<string[]>([]);
  const [sharedContactOwner, setSharedContactOwner] = useState<string[]>([]);
  const [sharedNeighbourhood, setSharedNeighbourhood] = useState<string | null>(
    null
  );
  const [sharedCreatedDate, setSharedCreatedDate] = useState<string | null>(
    null
  );
  const [sharedLastActivity, setSharedLastActivity] = useState<string | null>(
    null
  );
  const [deleteTarget, setDeleteTarget] = useState<CrmViewItem | null>(null);
  const [isDeletingView, setIsDeletingView] = useState(false);
  const [openLeadStageChangeToLostModal, setOpenLeadStageChangeToLostModal] =
    useState(false);
  const [pendingLostStageChange, setPendingLostStageChange] = useState<{
    leadId: string;
    fromStageId: string;
    toStageId: string;
    leadData?: Lead;
  } | null>(null);

  const viewId = activeTab !== "all" ? activeTab : undefined;

  const { data: leadStages } = useGetAllLeadStagesQuery(
    viewId ? { view_id: viewId } : {},
    { refetchOnMountOrArgChange: true }
  );

  const { data: pipelinesData } = useGetCrmPipelinesQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );

  const stageLostMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    if (!pipelinesData) return map;
    (pipelinesData as Array<{ stages: Array<{ key?: string; stage?: string; is_lost?: boolean }> }>).forEach((p) => {
      p.stages?.forEach((s) => {
        const stageKey = s.key || s.stage;
        if (stageKey) map[stageKey] = s.is_lost === true;
      });
    });
    return map;
  }, [pipelinesData]);

  const globalCrmFilters = useMemo(() => {
    return getGlobalCrmFilters(leadsFilters);
  }, [leadsFilters]);

  const crmViewId = selectedViewId !== "all_leads" ? selectedViewId : undefined;

  const crmFiltersForFetch = useMemo(() => {
    if (crmViewId) return {};
    return globalCrmFilters;
  }, [globalCrmFilters, crmViewId]);

  const selectedView = useMemo(
    () => crmViews?.find((v) => v?.id === selectedViewId),
    [crmViews, selectedViewId]
  );

  const globalLeadStageValues = useMemo(
    () => leadsFilters?.leadStage?.value ?? [],
    [leadsFilters?.leadStage?.value]
  );
  const globalLeadStageSig = useMemo(
    () => globalLeadStageValues.join(","),
    [globalLeadStageValues]
  );

  useEffect(() => {
    const stagesReady = Object.keys(stages ?? {}).length > 0;
    if (!stagesReady) return;

    const targetSignature = `${selectedViewId}|${viewRefreshTrigger}|${globalLeadStageSig}`;
    // Wait until the stages currently in Redux were actually loaded for this
    // (selectedViewId, viewRefreshTrigger, globalLeadStage) tuple — otherwise we'd
    // auto-collapse against stale data from the previous request.
    if (stagesLoadedSignature !== targetSignature) return;
    if (autoCollapseSignature === targetSignature) return;

    const filterGroups = selectedView?.filter_groups
      ? normalizeFilterGroups(
          selectedView.filter_groups as Parameters<
            typeof normalizeFilterGroups
          >[0]
        )
      : [];
    const hasViewCurrentStageFilter = filterGroups?.some((g) =>
      g?.conditions?.some(
        (c) => c?.key === "current_stage" && (c?.value?.length ?? 0) > 0
      )
    );
    const hasGlobalCurrentStageFilter =
      selectedViewId === "all_leads" && globalLeadStageValues.length > 0;
    const hasCurrentStageFilter =
      hasViewCurrentStageFilter || hasGlobalCurrentStageFilter;

    if (hasCurrentStageFilter) {
      const zeroCountIds = Object.values(stages ?? {})
        .filter((s) => (s?.count ?? 0) === 0)
        .map((s) => s?.stage)
        .filter((id): id is string => Boolean(id));
      dispatch(setCollapsedStageIds(zeroCountIds));
    } else {
      dispatch(clearCollapsedStages());
    }
    dispatch(setAutoCollapseSignature(targetSignature));
  }, [
    selectedViewId,
    viewRefreshTrigger,
    selectedView,
    stages,
    autoCollapseSignature,
    stagesLoadedSignature,
    globalLeadStageSig,
    globalLeadStageValues,
    dispatch,
  ]);

  const tableFilters = useMemo(() => {
    const filters: Record<string, unknown> = {};
    if (sharedContactOwner.length > 0) {
      const hasUnassigned = sharedContactOwner.includes("unassigned");
      const agentIds = sharedContactOwner.filter((id) => id !== "unassigned");
      if (hasUnassigned) filters.is_unassigned = true;
      if (agentIds.length > 0) filters.assigned_agents = agentIds;
    }
    if (sharedNeighbourhood) {
      if (sharedNeighbourhood === "no_property_assigned") {
        filters.properties = [];
        filters.has_no_property = true;
      } else {
        filters.properties = [sharedNeighbourhood];
        filters.has_no_property = false;
      }
    }
    if (sharedLastActivity) {
      const range = getDateRangeFromPreset(sharedLastActivity);
      if (range) filters.updated_after = range.startDate;
    }
    return filters;
  }, [sharedContactOwner, sharedNeighbourhood, sharedLastActivity]);

  const createdDatePresetKeys = useMemo(
    () => new Set(CREATED_DATE_PRESETS.map((p) => p.key)),
    []
  );

  useEffect(() => {
    const raw = leadsFilters?.duration?.value;
    const globalValue = typeof raw === "string" ? raw : null;
    const synced =
      globalValue && createdDatePresetKeys.has(globalValue)
        ? globalValue
        : null;
    setSharedCreatedDate(synced);
  }, [leadsFilters?.duration?.value, createdDatePresetKeys]);

  const createLeadViewId = useMemo(() => {
    if (activeTab !== "all") return activeTab;
    return pipelines[0]?.pipeline_key ?? "enquiry";
  }, [activeTab, pipelines]);

  const fetchFilteredLeadsByStageAbortRef = useRef<{
    abort: () => void;
  } | null>(null);

  const pipelineGroups: PipelineGroup[] = useMemo(
    () => buildPipelineGroups(pipelines),
    [pipelines]
  );

  const tabs = useMemo(() => {
    const pipelineTabs = pipelinesData?.map(
      (p: { key: string; display_name: string }) => ({
        key: p.key,
        label: p.display_name,
      })
    );
    if (pipelineTabs) {
      const hasAll = pipelineTabs.some((t: { key: string }) => t.key === "all");
      return hasAll ? pipelineTabs : [{ key: "all", label: "All" }, ...pipelineTabs];
    }
    return pipelineTabs;
  }, [pipelinesData]);

  const { stageColorMap, stageNameMap } = useMemo(() => {
    if (!leadStages) return { stageColorMap: {}, stageNameMap: {} };

    const stageColorMap: Record<string, string> = {};
    const stageNameMap: Record<string, string> = {};

    leadStages?.forEach(
      (stage: { code: string; name: string; base_color: string }) => {
        stageColorMap[stage?.code] = stage?.base_color;
        stageNameMap[stage?.code] = stage?.name;
      }
    );

    return { stageColorMap, stageNameMap };
  }, [leadStages]);

  const stageInfoRows = useMemo(() => buildStageInfoRows(leadStages), [leadStages]);

  const getStageViewId = useCallback(
    (stageId: string): string | undefined => {
      if (activeTab === "all") {
        const matched = pipelines.find((p) =>
          p.stages.some((s) => s.stage === stageId)
        );
        return matched?.pipeline_key;
      }
      return viewId;
    },
    [pipelines, activeTab, viewId]
  );

  const toggleStageCollapse = useCallback(
    (stageId: string) => {
      dispatch(toggleCollapsedStage(stageId));
    },
    [dispatch]
  );

  const handleLeadApiSearch = useCallback(
    async (query: string): Promise<LeadSuggestionItem[]> => {
      setSearchQuery(query.trim());
      if (!query.trim()) {
        return [];
      }
      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { start_date, end_date, ...filtersWithoutDates } =
          globalCrmFilters || {};
        const result = await getLeadList({
          search_query: query,
          chatType: "leads_created",
          ...filtersWithoutDates,
          limit: 100,
          cursor: undefined,
          is_crm: true,
        }).unwrap();

        if (result?.lead_cards) {
          return result?.lead_cards?.map((lead: Lead): LeadSuggestionItem => {
            return {
              id: lead?.lead_id,
              value: lead?.lead_id || "Anonymous",
              content: (
                <div className="p-1 flex items-center justify-between gap-[8px]">
                  <div className="flex items-center gap-[5px] min-w-0">
                    <Avatar text={lead?.name || "Anonymous"} size={24} />
                    <TruncatedTextTooltip
                      text={lead?.name || "Anonymous"}
                      maxWidth="max-w-[160px]"
                      className="text-data dark:text-data-dk font-medium text-md"
                    />
                  </div>
                  <div className="flex items-center gap-[8px]">
                    <LeadTypeBadge originalLeadId={lead?.original_lead_id} />
                    <p className="text-md text-dark-gray dark:text-dark-gray-dk font-normal">
                      #{lead?.lead_id}
                    </p>
                  </div>
                </div>
              ),
            };
          });
        }
        return [];
      } catch (error) {
        console.error("Lead search failed:", error);
        return [];
      }
    },
    [getLeadList, globalCrmFilters]
  );

  const handleLeadSelect = useCallback(
    (leadId: string, modality?: string) => {
      trackEvents(CRM_EVENTS.LEAD_CARD_OPENED, {
        lead_id: leadId,
        module: "crm",
        org_id: getClientDomain(),
      });

      navigate(
        `/dashboard/crm/${leadId}${modality ? `?modality=${modality}` : ""}`
      );
    },
    [navigate]
  );

  const handleExport = useCallback(
    async ({
      exportName,
      fileFormat,
    }: {
      exportName: string;
      fileFormat: string;
    }) => {
      const basePayload = {
        entity_type: "lead",
        file_format: fileFormat,
        file_name_hint: exportName,
      };
      const payload = crmViewId
        ? { ...basePayload, entity_filter: { view_id: crmViewId } }
        : {
            ...basePayload,
            entity_filter: cleanObject({
              ...globalCrmFilters,
              ...(searchQuery && { search_query: searchQuery }),
            }),
          };
      try {
        await createDataExport(payload).unwrap();
        trackEvents(CRM_EVENTS.LEADS_EXPORTED, {
          file_format: fileFormat,
          module: "crm",
          org_id: getClientDomain(),
        });
        setIsExportModalOpen(false);
        setExportedFile({
          name: `${exportName}.${fileFormat}`,
          format: fileFormat,
        });
        setIsExportQueuedOpen(true);
      } catch {
        customToast("Failed to start export. Please try again.", "error");
      }
    },
    [crmViewId, globalCrmFilters, searchQuery, createDataExport]
  );

  const fetchLeadsData = useCallback(
    async (limit: number, skip: number) => {
      try {
        const response = await fetchLeads({
          limit,
          skip,
          ...crmFiltersForFetch,
          ...tableFilters,
          ...(crmViewId
            ? { view_id: crmViewId }
            : viewId
              ? { pipeline_id: viewId }
              : {}),
        });
        if ("data" in response) {
          const pipelinesData = response.data as Pipeline[];
          dispatch(setPipelines(pipelinesData));
          dispatch(setStages(buildStagesFromPipelines(pipelinesData, limit)));
          dispatch(
            setStagesLoadedSignature(
              `${crmViewId ?? "all_leads"}|${viewRefreshTrigger}|${globalLeadStageSig}`
            )
          );
        }
      } catch (error) {
        console.error("Error fetching leads:", error);
      }
    },

    [
      fetchLeads,
      crmFiltersForFetch,
      tableFilters,
      crmViewId,
      viewId,
      dispatch,
      viewRefreshTrigger,
      globalLeadStageSig,
    ]
  );

  const handleLoadMore = useCallback(
    async (stageId: string) => {
      if (fetchingStagesRef?.current[stageId]) return;

      const currentStage = stages[stageId];
      if (!currentStage) return;

      const currentSkip = currentStage?.skip || 10;
      const limit = 10;
      const currentFilters = cleanObject(currentStage?.filters || {});

      if (currentStage?.leads?.length >= currentStage?.count) {
        return;
      }

      fetchingStagesRef.current[stageId] = true;
      setLoadingStages((prev) => ({ ...prev, [stageId]: true }));

      const payload: {
        limit: number;
        skip: number;
        sort_by?: Record<string, unknown>;
        stage: string[];
      } = { limit, skip: currentSkip, stage: stageId ? [stageId] : [] };

      if (Object.keys(currentFilters)?.length > 0) {
        payload.sort_by = currentFilters;
      }

      const stageViewId = getStageViewId(stageId);
      try {
        const response = await fetchLeads({
          ...payload,
          ...crmFiltersForFetch,
          ...(crmViewId
            ? { view_id: crmViewId }
            : stageViewId
              ? { pipeline_id: stageViewId }
              : {}),
        });
        if ("data" in response) {
          const pipelinesData = response.data as Pipeline[];
          const newStage = pipelinesData
            .flatMap((p) => p.stages)
            .find((s) => s.stage === stageId);
          if (newStage) {
            dispatch(
              appendStageLeads({
                stageId,
                leads: newStage.leads,
                newSkip: currentSkip + limit,
                count: newStage.count,
              })
            );
          }
        }
      } catch (error) {
        console.error("Error loading more leads:", error);
      } finally {
        fetchingStagesRef.current[stageId] = false;
        setLoadingStages((prev) => ({ ...prev, [stageId]: false }));
      }
    },
    [
      fetchLeads,
      stages,
      crmFiltersForFetch,
      crmViewId,
      getStageViewId,
      dispatch,
    ]
  );

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>, stageId: string) => {
      if (fetchingStagesRef?.current[stageId]) return;

      const target = e?.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = target;

      if (scrollHeight - scrollTop - clientHeight < 50) {
        const currentStage = stages[stageId];
        if (currentStage && currentStage?.leads?.length < currentStage?.count) {
          handleLoadMore(stageId);
        }
      }
    },
    [stages, handleLoadMore]
  );

  const handleSortPopupToggle = useCallback((stageId: string) => {
    setOpenSortPopup((prev) => (prev === stageId ? null : stageId));
  }, []);

  const fetchFilteredLeadsByStage = useCallback(
    async (currStage: Stage) => {
      try {
        if (fetchFilteredLeadsByStageAbortRef?.current?.abort) {
          fetchFilteredLeadsByStageAbortRef?.current?.abort();
        }

        const currentFilters = cleanObject(currStage?.filters);
        const stageViewId = getStageViewId(currStage?.stage);
        const payload: Record<string, unknown> = {
          limit: 10,
          skip: 0,
          stage: currStage?.stage ? [currStage.stage] : [],
          ...crmFiltersForFetch,
          ...tableFilters,
          ...(crmViewId
            ? { view_id: crmViewId }
            : stageViewId
              ? { pipeline_id: stageViewId }
              : {}),
        };
        if (Object.keys(currentFilters)?.length > 0) {
          payload.sort_by = currentFilters;
        }
        const request = fetchLeads(payload);
        fetchFilteredLeadsByStageAbortRef.current = request;
        const response = await request?.unwrap();
        if (response) {
          const pipelinesData = response as Pipeline[];
          const newStageData = pipelinesData
            .flatMap((p) => p.stages)
            .find((s) => s.stage === currStage?.stage);
          if (newStageData) {
            dispatch(
              updateStageFilters({
                stageId: currStage?.stage,
                filters: currentFilters,
                leads: newStageData.leads,
                count: newStageData.count,
              })
            );
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
    [
      fetchLeads,
      crmFiltersForFetch,
      tableFilters,
      crmViewId,
      getStageViewId,
      dispatch,
    ]
  );

  const handleStageSortChange = useCallback(
    (
      stageId: string,
      field: keyof Stage["filters"] | "reset",
      value?: 1 | -1
    ) => {
      const current = stages[stageId];
      if (!current) return;
      const updatedStage: Stage = {
        ...current,
        skip: 0,
        filters:
          field === "reset"
            ? {}
            : {
                ...(current?.filters ?? {}),
                [field]: value as 1 | -1,
              },
      };
      fetchFilteredLeadsByStage(updatedStage);
    },
    [stages, fetchFilteredLeadsByStage]
  );

  const handleLeadUpdate = useCallback(
    (lead: Lead, currStageId: string) => {
      dispatch(
        updateLeadInStage({
          stageId: currStageId,
          lead,
        })
      );
    },
    [dispatch]
  );

  const handleDragEnd = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (event: any) => {
      const { operation, canceled } = event;

      if (canceled) return;

      const active = operation?.source;
      const over = operation?.target;

      if (!over || !active) return;

      const activeData = active?.data as {
        type: string;
        leadId: string;
        fromStageId: string;
      };
      const overData = over?.data as { type: string; stageId: string };

      if (
        activeData?.type === "lead" &&
        overData?.type === "stage" &&
        activeData?.fromStageId !== overData?.stageId
      ) {
        const leadId = activeData?.leadId;
        const fromStageId = activeData?.fromStageId;
        const toStageId = overData?.stageId;

        const leadData = stages[fromStageId]?.leads?.find(
          (l) => l?.lead_id === leadId
        );

        dispatch(
          moveLeadBetweenStages({
            leadId,
            fromStageId,
            toStageId,
          })
        );

        try {
          const needsReasonModal =
            stageLostMap[toStageId] === true ||
            toStageId === "lost" ||
            (toStageId === "cancelled" &&
              isCancelledReasonModalClient(getClientDomain()));

          if (needsReasonModal) {
            setPendingLostStageChange({
              leadId,
              fromStageId,
              toStageId,
              leadData: leadData || undefined,
            });
            setOpenLeadStageChangeToLostModal(true);
          } else {
            await updateLeadStage({
              leadId,
              current_stage: toStageId,
              lost_primary_reason: null,
              lost_reason: null,
              reason_text: null,
              cancelled_primary_reason: null,
              cancelled_reason: null,
              cancellation_reason_text: null,
            }).unwrap();

            if (
              (fromStageId === "lost" || fromStageId === "cancelled") &&
              leadData
            ) {
              dispatch(
                updateLeadInStage({
                  stageId: toStageId,
                  lead: {
                    ...leadData,
                    current_stage: toStageId,
                    lost_primary_reason: null,
                    lost_reason: null,
                    reason_text: null,
                    cancelled_primary_reason: null,
                    cancelled_reason: null,
                    cancellation_reason_text: null,
                  },
                })
              );
            }

            trackEvents(CRM_EVENTS.LEAD_STAGE_CHANGED_BY_DRAG, {
              lead_id: leadId,
              from_stage: fromStageId,
              to_stage: toStageId,
              module: "crm",
              org_id: getClientDomain(),
            });

            customToast("Lead stage updated successfully", "success");
          }
        } catch (error) {
          dispatch(
            moveLeadBetweenStages({
              leadId,
              fromStageId: toStageId,
              toStageId: fromStageId,
            })
          );
          customToast("Failed to update lead stage", "error");
          console.error("Error updating lead stage:", error);
        }
      }
    },
    [dispatch, updateLeadStage, stages, stageLostMap]
  );

  const handleLostModalClose = useCallback(() => {
    if (pendingLostStageChange) {
      dispatch(
        moveLeadBetweenStages({
          leadId: pendingLostStageChange?.leadId,
          fromStageId: pendingLostStageChange?.toStageId,
          toStageId: pendingLostStageChange?.fromStageId,
        })
      );
    }
    setOpenLeadStageChangeToLostModal(false);
    setPendingLostStageChange(null);
  }, [dispatch, pendingLostStageChange]);

  const handleLostModalSubmit = useCallback(
    async (
      _stage: string,
      reasonData: {
        lost_primary_reason: string | null;
        lost_reason?: string | null;
        reason_text: string | null;
        cancelled_primary_reason?: string | null;
        cancelled_reason?: string | null;
        cancellation_reason_text?: string | null;
      }
    ) => {
      if (pendingLostStageChange?.leadData) {
        dispatch(
          updateLeadInStage({
            stageId: pendingLostStageChange?.toStageId,
            lead: {
              ...pendingLostStageChange?.leadData,
              current_stage: pendingLostStageChange?.toStageId,
              lost_primary_reason: reasonData?.lost_primary_reason ?? null,
              ...(reasonData?.lost_reason
                ? { lost_reason: reasonData?.lost_reason }
                : {}),
              reason_text: reasonData?.reason_text ?? null,
              cancelled_primary_reason:
                reasonData?.cancelled_primary_reason ?? null,
              ...(reasonData?.cancelled_reason
                ? { cancelled_reason: reasonData?.cancelled_reason }
                : {}),
              cancellation_reason_text:
                reasonData?.cancellation_reason_text ?? null,
            },
          })
        );
      }
      setOpenLeadStageChangeToLostModal(false);
      setPendingLostStageChange(null);
    },
    [pendingLostStageChange, dispatch]
  );

  useEffect(() => {
    setIsInitialLoad(true);
    fetchLeadsData(10, 0).finally(() => setIsInitialLoad(false));
  }, [fetchLeadsData, globalCrmFilters]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      try {
        const target = event?.target as Element;
        const isPopupContent = target?.closest?.("[data-crm-popup-content]");
        const isPopupTrigger = target?.closest?.("[data-crm-popup-trigger]");

        if (!isPopupContent && !isPopupTrigger) {
          setOpenSortPopup(null);
        }
      } catch {
        setOpenSortPopup(null);
      }
    };

    if (openSortPopup) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [openSortPopup]);

  useEffect(() => {
    if (crmViewsData?.views) {
      dispatch(setCrmViews(crmViewsData.views));
    }
  }, [crmViewsData, dispatch]);

  useEffect(() => {
    if (newlyCreatedViewId) {
      dispatch(setSelectedViewId(newlyCreatedViewId));
      dispatch(setNewlyCreatedViewId(null));
    }
  }, [newlyCreatedViewId, dispatch]);

  const handleDeleteView = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeletingView(true);
    try {
      await deleteCrmViewApi(deleteTarget.id).unwrap();
      trackEvents(CRM_EVENTS.VIEW_DELETED, {
        view_id: deleteTarget.id,
        view_name: deleteTarget.name ?? "",
        module: "crm",
        org_id: getClientDomain(),
      });
      dispatch(removeCrmView(deleteTarget.id));
      customToast("View deleted successfully", "success");
      setDeleteTarget(null);
    } catch {
      customToast("Failed to delete view", "error");
    } finally {
      setIsDeletingView(false);
    }
  }, [deleteTarget, deleteCrmViewApi, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetStages());
    };
  }, [dispatch]);

  const isListView = activeView === "list";

  const visibleColumnKeys = useMemo(
    () => columnConfig.filter((c) => c.visible).map((c) => c.key),
    [columnConfig]
  );

  const handleColumnPin = useCallback((columnId: string) => {
    setPinnedColumns((prev) => {
      if (prev.includes(columnId)) {
        return prev.filter((id) => id !== columnId);
      }
      if (prev.length >= 4) return prev;
      return [...prev, columnId];
    });
  }, []);

  const handlePinnedCountChange = useCallback(
    (count: number) => {
      setPinnedColumns(visibleColumnKeys.slice(0, count));
    },
    [visibleColumnKeys]
  );

  const handleContactOwnerApply = useCallback((ids: string[]) => {
    setSharedContactOwner(ids);
  }, []);

  const handleContactOwnerReset = useCallback(() => {
    setSharedContactOwner([]);
  }, []);

  const handleNeighbourhoodSelect = useCallback((id: string | null) => {
    setSharedNeighbourhood(id);
  }, []);

  const handleCreatedDateSelect = useCallback(
    (preset: string | null) => {
      setSharedCreatedDate(preset);
      if (preset) {
        const range = getDateRangeFromPreset(preset);
        if (range) {
          dispatch(
            setCrmLeadsDuration({
              startDate: range.startDate,
              endDate: range.endDate,
              value: preset,
              displayValue: range.displayValue,
            })
          );
        }
      } else {
        dispatch(clearCrmLeadsDuration());
      }
    },
    [dispatch]
  );

  const handleLastActivitySelect = useCallback((preset: string | null) => {
    setSharedLastActivity(preset);
  }, []);

  const renderKanbanView = () => (
    <DragDropProvider onDragEnd={handleDragEnd}>
      <div
        className={`flex flex-1 min-h-0 h-full rounded-[6px] overflow-x-auto overflow-y-hidden ${visibleScrollbar(
          "[&::-webkit-scrollbar]:h-[8px] [&::-webkit-scrollbar]:w-[8px]"
        )}`}
      >
        {isInitialLoad ? (
          <div className="p-3 flex gap-[8px]">
            {Array?.from({ length: 5 })?.map((_, i) => (
              <LeadStagesShimmer key={i} />
            ))}
          </div>
        ) : (
          <div className="flex gap-2">
            {pipelineGroups?.map((group) => (
              <div
                key={group.pipelineKey}
                className={`flex flex-col shrink-0 rounded-[6px] border-border-gray dark:border-border-gray-dk ${
                  tabs?.length > 1
                    ? "bg-hover-effect dark:bg-hover-effect-dk"
                    : ""
                }`}
              >
                {tabs?.length > 1 && (
                  <KanBanHeader pipelineName={group.pipelineName} />
                )}
                <div
                  className={`flex gap-[8px] flex-1 min-h-0 ${tabs?.length > 1 ? "p-3" : ""}`}
                >
                  {group?.stageIds?.map((stageId) => {
                    const stage = stages[stageId];
                    if (!stage) return null;
                    const isCollapsed = collapsedStageIds?.includes(
                      stage?.stage
                    );
                    return (
                      <div
                        key={stage?.stage}
                        className="shrink-0 flex flex-col gap-[8px] h-full min-h-0"
                      >
                        {!isCollapsed && (
                          <div
                            className="w-[300px] rounded-[8px] px-[12px] py-[9px] flex justify-between items-center gap-[8px]"
                            style={{
                              backgroundColor: stage?.base_color,
                            }}
                          >
                            <div className="flex items-center gap-[8px] min-w-0">
                              <TruncatedTextTooltip
                                text={stage?.name}
                                maxWidth="max-w-[240px]"
                                className="uppercase text-md font-bold text-fixed-white dark:text-fixed-white-dk"
                              />
                              <div className="bg-black/15 px-[8px] py-[1.5px] rounded-[24px]">
                                <p className="text-sm font-semibold text-fixed-white dark:text-fixed-white-dk">
                                  {stage?.count}
                                </p>
                              </div>
                            </div>
                            <div className="relative flex items-center justify-center gap-[8px]">
                              <button onClick={() => setIsStageInfoOpen(true)}>
                                <InfoBtnIcon className="w-[16px] h-[16px] text-fixed-white dark:text-fixed-white-dk" />
                              </button>
                              <button
                                onClick={() =>
                                  toggleStageCollapse(stage?.stage)
                                }
                              >
                                <PlayIcon className="w-[16px] h-[16px] text-fixed-white dark:text-fixed-white-dk rotate-180" />
                              </button>
                              {stage?.count > 0 && (
                                <>
                                  <button
                                    data-crm-popup-trigger
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleSortPopupToggle(stage?.stage);
                                    }}
                                  >
                                    <SortByIcon className="w-[16px] h-[16px] text-fixed-white dark:text-fixed-white-dk" />
                                  </button>
                                  {openSortPopup === stage?.stage && (
                                    <LeadStageSortPopup
                                      stage={stage}
                                      onSortChange={handleStageSortChange}
                                      onClose={() =>
                                        handleSortPopupToggle(
                                          stage?.stage ?? "null"
                                        )
                                      }
                                    />
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        )}

                        <DroppableStage
                          stageId={stage?.stage}
                          containerClassName="flex-1 min-h-0"
                        >
                          {isCollapsed ? (
                            <>
                              <button
                                onClick={() =>
                                  toggleStageCollapse(stage?.stage)
                                }
                                className="flex items-center px-[8px] py-[12px] rounded-[8px] cursor-pointer"
                                style={{
                                  backgroundColor: stage?.base_color,
                                }}
                              >
                                <div className="flex flex-col gap-[12px] items-center w-[20px]">
                                  <PlayIcon className="w-[16px] h-[16px] text-fixed-white dark:text-fixed-white-dk shrink-0" />
                                  <div
                                    className="flex items-center justify-center"
                                    style={{
                                      writingMode: "vertical-rl",
                                    }}
                                  >
                                    <div
                                      className="flex items-center gap-[8px]"
                                      style={{
                                        transform: "rotate(180deg)",
                                      }}
                                    >
                                      <span className="uppercase text-sm font-bold text-fixed-white dark:text-fixed-white-dk whitespace-nowrap">
                                        {stage?.name}
                                      </span>
                                      <div className="bg-black/15 px-[8px] py-[2px] rounded-[24px]">
                                        <span className="text-sm font-semibold text-fixed-white dark:text-fixed-white-dk">
                                          {stage?.count}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </button>
                              <div className="flex-1" />
                            </>
                          ) : (
                            <div
                              className="flex flex-col gap-[8px] flex-1 min-h-0 overflow-y-auto overflow-x-hidden no-scrollbar pb-2"
                              onScroll={(e) => handleScroll(e, stage?.stage)}
                              data-crm-stage-scroll-container
                            >
                              {stage?.leads?.length > 0 &&
                                stage?.leads?.map((lead) => (
                                  <LeadKanbanCard
                                    key={lead?.lead_id}
                                    lead={lead}
                                    onSelect={handleLeadSelect}
                                    stageId={stage?.stage}
                                    handleLeadUpdate={handleLeadUpdate}
                                  />
                                ))}
                              {loadingStages[stage?.stage] && (
                                <div className="flex items-center justify-center pt-2">
                                  <PulseLoader size={6} />
                                </div>
                              )}
                            </div>
                          )}
                        </DroppableStage>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DragDropProvider>
  );

  const renderListView = () => (
    <div
      className="flex flex-col gap-5 flex-1 min-h-0 overflow-y-auto pb-4"
    >
      {isInitialLoad ? (
        <div className="p-3 flex gap-[8px]">
          {Array?.from({ length: 5 })?.map((_, i) => (
            <LeadStagesShimmer key={i} />
          ))}
        </div>
      ) : (
        pipelineGroups?.flatMap((group) =>
          group?.stageIds?.map((stageId) => {
            const stage = stages[stageId];
            if (!stage) return null;
            return (
              <LeadTableListing
                key={stageId}
                stage={stage}
                viewId={getStageViewId(stageId)}
                crmViewId={crmViewId}
                globalCrmFilters={globalCrmFilters}
                tableFilters={tableFilters}
                stageColorMap={stageColorMap}
                stageNameMap={stageNameMap}
                visibleColumnKeys={visibleColumnKeys}
                pinnedColumns={pinnedColumns}
                onPinColumn={handleColumnPin}
                onLeadUpdate={handleLeadUpdate}
                contactOwner={sharedContactOwner}
                neighbourhood={sharedNeighbourhood}
                onContactOwnerApply={handleContactOwnerApply}
                onContactOwnerReset={handleContactOwnerReset}
                onNeighbourhoodSelect={handleNeighbourhoodSelect}
                createdDate={sharedCreatedDate}
                lastActivity={sharedLastActivity}
                onCreatedDateSelect={handleCreatedDateSelect}
                onLastActivitySelect={handleLastActivitySelect}
                onInfoClick={() => setIsStageInfoOpen(true)}
              />
            );
          })
        )
      )}
    </div>
  );

  return (
    <>
      <div className="flex flex-col w-full h-full bg-pure-white dark:bg-pure-white-dk">
        <Header>
          <div className="flex items-center">
            <div className="flex items-center gap-[8px]">
              <h1 className="text-xl font-semibold text-verba-indigo dark:text-verba-indigo-dk">
                CRM
              </h1>
              <hr className="border border-border-gray dark:border-border-gray-dk h-[16px] w-1px" />
              <p className="text-md font-normal text-heading-text dark:text-heading-text-dk">
                Leasing
              </p>
            </div>

            <div className="w-[280px] mx-auto">
              <SearchBar
                id="crm-lead-search"
                placeholder="Search by lead id, name, email or phone"
                noSuggestionsText="No leads found"
                enableApiSearch={true}
                onApiSearch={handleLeadApiSearch}
                onSelect={(suggestion) => handleLeadSelect(suggestion)}
                apiSearchDebounceMs={0}
                minCharsToSearch={1}
                suggestionsClassName="max-h-[300px]"
                containerClassName="w-[280px]"
                className="pl-[8px] pr-[8px] pt-[7px] pb-[7px] hover:border-b-2 hover:border-b-verba-blue-dark-mode dark:hover:border-b-verba-blue hover:rounded-[6px]"
              />
            </div>

            {selectedViewId === "all_leads" && (
              <div className="flex items-center gap-[12px]">
                <Button
                  innerText={
                    <span className="flex items-center gap-1">
                      Filters
                      {filterCount > 0 && (
                        <span className="ml-1 rounded-full bg-fixed-white-light-and-dark-mode dark:bg-fixed-white-light-and-dark-mode-dk text-verba-blue dark:text-verba-blue-dk px-2 py-0.5 text-sm font-semibold">
                          {filterCount}
                        </span>
                      )}
                    </span>
                  }
                  buttonContainerClass="py-2.5 px-3 bg-verba-blue dark:bg-verba-blue-dk rounded-full"
                  innerTextClass="text-fixed-white-light-and-dark-mode dark:text-fixed-white-light-and-dark-mode-dk text-md font-semibold"
                  Icon={FilterV2Icon}
                  onClick={() => setIsFiltersOpen(true)}
                  iconClass="w-[18px] h-[18px] text-fixed-white-light-and-dark-mode dark:text-fixed-white-light-and-dark-mode-dk"
                  iconLeft={true}
                />
                <div className="w-px h-[12px] bg-border-gray dark:bg-border-gray-dk" />
              </div>
            )}
          </div>
        </Header>

        <div className="flex flex-col gap-4 p-4 pb-0 w-full flex-1 min-h-0 overflow-hidden border-l border-t rounded-tl-[6px] border-border-gray dark:border-border-gray-dk">
          <KanbanActionBar
            activeTab={activeTab}
            onTabChange={(tab) => {
              dispatch(setActiveTab(tab));
              dispatch(setSelectedViewId("all_leads"));
            }}
            tabs={tabs}
            crmViews={crmViews}
            selectedViewId={selectedViewId}
            onSelectView={(id) => {
              dispatch(setSelectedViewId(id));
              if (id !== "all_leads") dispatch(setActiveTab("all"));
            }}
            onCreateClick={() =>
              dispatch(
                setCrmDrawer({
                  open: true,
                  type: "create_lead",
                  id: createLeadViewId,
                })
              )
            }
            viewTabs={
              <CrmViewTabs
                views={crmViews}
                selectedViewId={selectedViewId}
                onSelectView={(id) => {
                  dispatch(setSelectedViewId(id));
                  if (id !== "all_leads") dispatch(setActiveTab("all"));
                }}
                onDeleteView={setDeleteTarget}
              />
            }
            editColumnsButton={
              <>
                <button
                  onClick={() =>
                    dispatch(
                      setCrmDrawer({
                        open: true,
                        type: "crm_view",
                      })
                    )
                  }
                  className="flex items-center gap-1 text-md font-medium text-verba-blue dark:text-verba-blue-dk whitespace-nowrap hover:underline cursor-pointer"
                >
                  <PlusFilledIcon className="w-[16px] h-[16px] text-verba-blue dark:text-verba-blue-dk" />
                  Add a View
                </button>
                <div className="bg-border-gray dark:bg-border-gray-dk h-[12px] w-px" />
                <button
                  onClick={() => setIsExportModalOpen(true)}
                  className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk border border-border-gray dark:border-border-gray-dk rounded-md px-3 py-1 hover:bg-hover-effect dark:hover:bg-hover-effect-dk whitespace-nowrap"
                >
                  Export
                </button>
              </>
            }
            viewToggle={
              <ViewToggle
                view={activeView}
                onViewChange={(view) => dispatch(setActiveView(view))}
              />
            }
          />

          {isFiltersOpen && (
            <Filters
              openDrawer={isFiltersOpen}
              handleDrawerClose={() => setIsFiltersOpen(false)}
              size="380px"
              filterType="crm"
              modalities={[]}
            />
          )}

          {isListView ? renderListView() : renderKanbanView()}
        </div>
      </div>

      <EditColumnsModal
        open={isEditColumnsOpen}
        onOpenChange={setIsEditColumnsOpen}
        columns={columnConfig}
        onApply={setColumnConfig}
        pinnedCount={pinnedColumns.length}
        onPinnedCountChange={handlePinnedCountChange}
      />

      <ExportContactsModal
        open={isExportModalOpen}
        onOpenChange={setIsExportModalOpen}
        totalCount={Object.values(stages).reduce(
          (sum, s) => sum + (s?.count || 0),
          0
        )}
        defaultName={
          selectedViewId !== "all_leads"
            ? (selectedView?.name ?? "All Contacts")
            : activeTab !== "all"
              ? (tabs?.find(
                  (t: { key: string; label: string }) => t?.key === activeTab
                )?.label ?? "All Contacts")
              : "All Contacts"
        }
        loading={isExporting}
        onExport={handleExport}
      />

      {exportedFile && (
        <ExportQueuedModal
          open={isExportQueuedOpen}
          onOpenChange={setIsExportQueuedOpen}
          entityName={exportedFile?.name ?? ""}
          fileName={exportedFile?.name ?? ""}
          onViewImportsExports={() =>
            navigate("/dashboard/settings/imports_and_exports")
          }
        />
      )}

      <CrmCreateEditView />

      {deleteTarget && (
        <CrmDeleteViewModal
          open={!!deleteTarget}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
          onConfirmDelete={handleDeleteView}
          view={deleteTarget}
          isDeleting={isDeletingView}
        />
      )}

      <LeadStageChangeToLostModal
        leadId={pendingLostStageChange?.leadId ?? ""}
        open={openLeadStageChangeToLostModal}
        onOpenChange={setOpenLeadStageChangeToLostModal}
        currentStage={pendingLostStageChange?.fromStageId ?? ""}
        newStage={pendingLostStageChange?.toStageId ?? ""}
        stageColorMap={stageColorMap}
        stageNameMap={stageNameMap}
        onStageUpdate={handleLostModalSubmit}
        onClose={handleLostModalClose}
      />

      <LeadStagesInfoModal
        open={isStageInfoOpen}
        onOpenChange={setIsStageInfoOpen}
        stages={stageInfoRows}
      />
    </>
  );
};

export default LeadList;
